import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import { useShop, useServices, useTokens } from "../lib/hooks";
import AnalyticsPanel from "../components/AnalyticsPanel";
import ShopSettingsPanel from "../components/ShopSettingsPanel";
import ShopLinksPanel from "../components/ShopLinksPanel";
import ManualEntryModal from "../components/ManualEntryModal";
import EditTokenServicesModal from "../components/EditTokenServicesModal";
import {
  updateShopOpen,
  updateTokenStatus,
  createService,
  updateService,
  deleteService,
  notifyWhatsAppViaShop,
  isShopSuspended,
  updateShop,
} from "../lib/api";
import { verifyPin, hashPin, isHashed } from "../lib/pinSecurity";
import BrandHeader from "../components/BrandHeader";
import { FullPageLoader, ShopMissing } from "../components/States";
import SuspendedGate from "../components/SuspendedGate";
import {
  PhoneCall,
  Check,
  RotateCcw,
  Plus,
  Trash2,
  Pencil,
  Power,
  Lock,
  Delete,
  X,
  Tv,
  ExternalLink,
  History,
  UserPlus,
} from "lucide-react";

const PIN_LEN = 4;

export default function AdminDashboardPage() {
  const { slug } = useParams();
  const { shop, loading: shopLoading } = useShop(slug);
  // Auth is in-memory only (PIN required on every refresh). Safer than sessionStorage
  // against XSS — no auth token is ever persisted to a script-accessible store.
  const [authed, setAuthed] = useState(false);

  if (shopLoading) return <FullPageLoader label="Opening Control Room" />;
  if (!shop) return <ShopMissing slug={slug} />;
  const suspended = isShopSuspended(shop);
  if (suspended) return <SuspendedGate shop={shop} reason={suspended.reason} />;

  if (!authed) {
    return <PinScreen shop={shop} onSuccess={() => setAuthed(true)} />;
  }

  return <ControlRoom shop={shop} slug={slug} />;
}

// ============================================================
// PIN SCREEN
// ============================================================
function PinScreen({ shop, onSuccess }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const [checking, setChecking] = useState(false);

  function press(d) {
    if (checking) return;
    setError(false);
    if (pin.length >= PIN_LEN) return;
    const next = pin + d;
    setPin(next);
    if (next.length === PIN_LEN) {
      setTimeout(() => attempt(next), 120);
    }
  }
  function backspace() {
    setError(false);
    setPin((p) => p.slice(0, -1));
  }
  async function attempt(value) {
    setChecking(true);
    try {
      const ok = await verifyPin(value, shop.admin_pin);
      if (ok) {
        // Silent upgrade: legacy plaintext PIN gets hashed once verified.
        if (!isHashed(shop.admin_pin)) {
          try {
            const hashed = await hashPin(value);
            await updateShop(shop.id, { admin_pin: hashed });
          } catch {/* non-fatal — login still succeeds */}
        }
        onSuccess();
      } else {
        setError(true);
        setShake(true);
        setTimeout(() => {
          setPin("");
          setShake(false);
        }, 500);
      }
    } finally {
      setChecking(false);
    }
  }

  const dots = Array.from({ length: PIN_LEN }, (_, i) => `dot-${i}`);

  function dotClassName(idx) {
    if (idx >= pin.length) return "border-white/30";
    if (error) return "bg-red-500 border-red-500";
    return "bg-gold border-gold";
  }

  return (
    <div className="min-h-screen bg-void text-white flex flex-col">
      <BrandHeader shop={shop} minimal />
      <div className="flex-1 flex items-center justify-center px-5 py-10">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-sm"
        >
          <div className="text-center">
            <div className="w-12 h-12 mx-auto rounded-full border border-gold/40 bg-void flex items-center justify-center gold-glow">
              <Lock className="w-5 h-5 text-gold" strokeWidth={1.5} />
            </div>
            <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-white/40 mt-5">
              Admin Console
            </div>
            <div className="font-heading text-2xl mt-2">Enter your PIN</div>
          </div>

          <motion.div
            animate={shake ? { x: [-8, 8, -6, 6, -3, 3, 0] } : {}}
            transition={{ duration: 0.45 }}
            className="mt-8 flex items-center justify-center gap-4"
            data-testid="pin-dots"
          >
            {dots.map((dotKey, i) => (
              <div
                key={dotKey}
                className={`w-3.5 h-3.5 rounded-full border ${dotClassName(i)}`}
              />
            ))}
          </motion.div>

          {error && (
            <div className="mt-4 text-center text-sm text-red-400" data-testid="pin-error">
              Incorrect PIN. Try again.
            </div>
          )}

          <div className="mt-4 text-center text-[10px] font-numbers tracking-[0.25em] uppercase text-white/40">
            Forgot PIN? Contact AIBoxHub support to reset.
          </div>

          <div className="mt-8 grid grid-cols-3 gap-3">
            {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
              <button
                key={d}
                onClick={() => press(d)}
                data-testid={`pin-${d}`}
                className="glass rounded-2xl h-16 font-numbers text-2xl text-white hover:border-gold/40 active:scale-95 transition-all"
              >
                {d}
              </button>
            ))}
            <div />
            <button
              onClick={() => press("0")}
              data-testid="pin-0"
              className="glass rounded-2xl h-16 font-numbers text-2xl text-white hover:border-gold/40 active:scale-95 transition-all"
            >
              0
            </button>
            <button
              onClick={backspace}
              data-testid="pin-backspace"
              className="rounded-2xl h-16 text-white/60 hover:text-gold flex items-center justify-center transition-colors"
            >
              <Delete className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// ============================================================
// CONTROL ROOM
// ============================================================
function ControlRoom({ shop, slug }) {
  const { services } = useServices(shop.id);
  const { tokens } = useTokens(shop.id);
  const [showManual, setShowManual] = useState(false);
  const [editingToken, setEditingToken] = useState(null);

  const serving = tokens.find((t) => t.status === "serving");
  const waiting = tokens.filter((t) => t.status === "waiting");
  const completedToday = tokens.filter((t) => t.status === "completed");
  const totalToday = tokens.length;

  async function callNext() {
    if (serving) {
      toast.error("Complete the current token first.");
      return;
    }
    const next = waiting[0];
    if (!next) {
      toast.message("No customers waiting.");
      return;
    }
    try {
      await updateTokenStatus(next.id, "serving");
      toast.success(`Calling #${next.token_number} · ${next.customer_name}`);
      // Fire-and-forget WhatsApp notification if configured
      if (shop.wa_enabled && shop.wa_webhook_url) {
        notifyWhatsAppViaShop(shop, next).catch((err) =>
          toast.error(`WhatsApp notify failed: ${err.message}`)
        );
      }
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function completeCurrent() {
    if (!serving) return;
    try {
      await updateTokenStatus(serving.id, "completed");
      toast.success(`#${serving.token_number} completed`);
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function recall() {
    if (!serving) return;
    try {
      // Re-trigger by toggling to waiting then back to serving
      await updateTokenStatus(serving.id, "waiting", { serving_started_at: null });
      await updateTokenStatus(serving.id, "serving");
      toast.success(`Recalled #${serving.token_number}`);
    } catch (e) {
      toast.error(e.message);
    }
  }

  async function toggleOpen() {
    try {
      await updateShopOpen(shop.id, !shop.is_open);
      toast.success(shop.is_open ? "Shop closed" : "Shop opened");
    } catch (e) {
      toast.error(e.message);
    }
  }

  return (
    <div className="min-h-screen bg-void text-white">
      <Toaster theme="dark" position="top-center" richColors />
      <BrandHeader
        shop={shop}
        right={
          <div className="flex items-center gap-2">
            <Link
              to={`/s/${slug}/history`}
              className="btn-ghost text-xs inline-flex items-center gap-1.5 py-1.5 px-3"
              data-testid="open-history-link"
            >
              <History className="w-3.5 h-3.5" /> History
            </Link>
            <Link
              to={`/s/${slug}/tv`}
              target="_blank"
              className="btn-ghost text-xs inline-flex items-center gap-1.5 py-1.5 px-3"
              data-testid="open-tv-link"
            >
              <Tv className="w-3.5 h-3.5" /> TV <ExternalLink className="w-3 h-3" />
            </Link>
            <button
              onClick={toggleOpen}
              data-testid="toggle-shop-open"
              className={`text-xs inline-flex items-center gap-1.5 py-1.5 px-3 rounded-full border transition-all ${
                shop.is_open
                  ? "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"
                  : "border-red-500/40 text-red-400 hover:bg-red-500/10"
              }`}
            >
              <Power className="w-3.5 h-3.5" />
              {shop.is_open ? "Shop Open" : "Shop Closed"}
            </button>
          </div>
        }
      />

      <main className="px-5 md:px-10 pb-16 max-w-7xl mx-auto">
        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Waiting" value={waiting.length} accent />
          <Stat label="Serving" value={serving ? 1 : 0} />
          <Stat label="Completed" value={completedToday.length} />
          <Stat label="Total Today" value={totalToday} />
        </div>

        {/* Now Serving + Actions */}
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 glass-strong rounded-2xl p-6 md:p-8 relative overflow-hidden">
            <div className="absolute -top-32 -right-32 w-72 h-72 rounded-full bg-gold/10 blur-3xl" />
            <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-white/40">
              Now Serving
            </div>
            <AnimatePresence mode="popLayout">
              <motion.div
                key={serving?.id || "none"}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -14 }}
                transition={{ duration: 0.45 }}
                data-testid="admin-now-serving"
              >
                {serving ? (
                  <div className="mt-2">
                    <div className="font-numbers font-semibold leading-none text-gold gold-text-glow text-[clamp(4rem,12vw,9rem)] tabular-nums">
                      {serving.token_number}
                    </div>
                    <div className="mt-4 font-heading text-2xl md:text-3xl">
                      {serving.customer_name}
                    </div>
                    <div className="font-numbers text-xs tracking-[0.3em] uppercase text-white/40 mt-1">
                      {serving.service_name} · {serving.whatsapp}
                    </div>
                  </div>
                ) : (
                  <div className="mt-6 font-heading text-2xl md:text-3xl text-white/40">
                    No active customer. Call the next token.
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            <div className="mt-8 flex flex-wrap gap-3">
              <button
                onClick={callNext}
                data-testid="btn-call-next"
                className="btn-gold inline-flex items-center gap-2"
                disabled={!shop.is_open}
              >
                <PhoneCall className="w-4 h-4" /> Call Next
              </button>
              <button
                onClick={completeCurrent}
                data-testid="btn-complete"
                className="btn-ghost inline-flex items-center gap-2"
                disabled={!serving}
              >
                <Check className="w-4 h-4" /> Complete
              </button>
              <button
                onClick={recall}
                data-testid="btn-recall"
                className="btn-ghost inline-flex items-center gap-2"
                disabled={!serving}
              >
                <RotateCcw className="w-4 h-4" /> Recall
              </button>
              <button
                onClick={() => setShowManual(true)}
                data-testid="btn-manual-entry"
                className="btn-ghost inline-flex items-center gap-2"
              >
                <UserPlus className="w-4 h-4" /> Add Walk-in
              </button>
            </div>
          </div>

          {/* Waiting list */}
          <div className="lg:col-span-5 rounded-2xl bg-charcoal border border-white/10 p-6">
            <div className="flex items-center justify-between">
              <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-white/40">
                Waiting Queue
              </div>
              <div className="font-numbers text-xs text-white/40 tabular-nums">
                {waiting.length} in line
              </div>
            </div>
            <div className="mt-4 space-y-2 max-h-[26rem] overflow-y-auto pr-1" data-testid="admin-waiting-list">
              {waiting.length === 0 ? (
                <div className="text-white/30 text-sm py-12 text-center">
                  Queue is empty.
                </div>
              ) : (
                waiting.map((t, i) => (
                  <div
                    key={t.id}
                    className="rounded-xl border border-white/10 px-4 py-3 flex items-center justify-between hover:border-gold/30 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="font-numbers text-xl tabular-nums text-gold w-14">
                        {t.token_number}
                      </div>
                      <div>
                        <div className="font-body text-sm">{t.customer_name}</div>
                        <div className="text-[10px] font-numbers tracking-[0.25em] uppercase text-white/40 mt-0.5">
                          {t.service_name || "—"}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingToken(t)}
                        data-testid={`edit-token-${t.id}`}
                        className="p-2 rounded-lg text-white/50 hover:text-gold hover:bg-white/5"
                        title="Add / edit services"
                      >
                        <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                      <div className="font-numbers text-[10px] tracking-[0.25em] text-white/30 uppercase">
                        #{i + 1}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Shop Links + QR + Share */}
        <div className="mt-8">
          <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-white/40 mb-3">
            Share & QR · Booking Links
          </div>
          <ShopLinksPanel shop={shop} />
        </div>

        {/* Services */}
        <div className="mt-8">
          <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-white/40 mb-3">
            Shop Settings · Branding & WhatsApp
          </div>
          <ShopSettingsPanel shop={shop} />
        </div>

        <div className="mt-8">
          <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-white/40 mb-3">
            Analytics · Live
          </div>
          <AnalyticsPanel shopId={shop.id} />
        </div>

        <ServicesManager shop={shop} services={services} />
      </main>

      {showManual && (
        <ManualEntryModal
          shop={shop}
          services={services}
          onClose={() => setShowManual(false)}
        />
      )}

      {editingToken && (
        <EditTokenServicesModal
          token={editingToken}
          services={services}
          onClose={() => setEditingToken(null)}
        />
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-2xl bg-charcoal border border-white/10 p-5">
      <div className="text-[10px] tracking-[0.3em] uppercase text-white/40 font-numbers">
        {label}
      </div>
      <div
        className={`mt-3 font-numbers text-3xl tabular-nums ${accent ? "text-gold" : "text-white"}`}
      >
        {value}
      </div>
    </div>
  );
}

// ============================================================
// SERVICES MANAGER
// ============================================================
function ServicesManager({ shop, services }) {
  const [editing, setEditing] = useState(null); // null | "new" | service object
  return (
    <div className="mt-8 rounded-2xl bg-charcoal border border-white/10 p-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-white/40">
            Services
          </div>
          <div className="font-heading text-xl mt-1">Catalog & Pricing</div>
        </div>
        <button
          onClick={() => setEditing("new")}
          data-testid="btn-add-service"
          className="btn-gold inline-flex items-center gap-2 text-sm py-2 px-4"
        >
          <Plus className="w-4 h-4" /> Add Service
        </button>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {services.map((s) => (
          <div
            key={s.id}
            className="rounded-xl border border-white/10 p-4 hover:border-gold/30 transition-colors"
            data-testid={`service-row-${s.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-body text-base">{s.name}</div>
                <div className="text-[10px] font-numbers tracking-[0.25em] uppercase text-white/40 mt-1">
                  {s.duration_minutes}m · ₹{Number(s.price).toLocaleString()}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setEditing(s)}
                  data-testid={`edit-service-${s.id}`}
                  className="p-2 rounded-lg hover:bg-white/5 text-white/60 hover:text-gold"
                >
                  <Pencil className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Delete "${s.name}"?`)) return;
                    try {
                      await deleteService(s.id);
                      toast.success("Service deleted");
                    } catch (e) {
                      toast.error(e.message);
                    }
                  }}
                  data-testid={`delete-service-${s.id}`}
                  className="p-2 rounded-lg hover:bg-white/5 text-white/60 hover:text-red-400"
                >
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between">
              <span
                className={`text-[10px] font-numbers tracking-[0.3em] uppercase ${s.is_active ? "text-emerald-400" : "text-white/30"}`}
              >
                {s.is_active ? "Active" : "Hidden"}
              </span>
              <button
                onClick={async () => {
                  try {
                    await updateService(s.id, { is_active: !s.is_active });
                  } catch (e) {
                    toast.error(e.message);
                  }
                }}
                data-testid={`toggle-service-${s.id}`}
                className="text-[10px] text-white/40 hover:text-gold font-numbers tracking-[0.3em] uppercase"
              >
                {s.is_active ? "Hide" : "Show"}
              </button>
            </div>
          </div>
        ))}
        {services.length === 0 && (
          <div className="col-span-full text-center text-white/40 py-10 text-sm">
            No services yet. Click "Add Service" to create one.
          </div>
        )}
      </div>

      <AnimatePresence>
        {editing && (
          <ServiceEditor
            shop={shop}
            initial={editing === "new" ? null : editing}
            onClose={() => setEditing(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function ServiceEditor({ shop, initial, onClose }) {
  const isNew = !initial;
  const [name, setName] = useState(initial?.name || "");
  const [duration, setDuration] = useState(initial?.duration_minutes || 30);
  const [price, setPrice] = useState(initial?.price || 0);
  const [sortOrder, setSortOrder] = useState(initial?.sort_order ?? 0);
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        duration_minutes: Number(duration) || 0,
        price: Number(price) || 0,
        sort_order: Number(sortOrder) || 0,
        is_active: isActive,
      };
      if (isNew) {
        await createService(shop.id, payload);
        toast.success("Service created");
      } else {
        await updateService(initial.id, payload);
        toast.success("Service updated");
      }
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  function saveLabel() {
    if (saving) return "Saving…";
    return isNew ? "Create Service" : "Save Changes";
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-5"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.96 }}
        transition={{ duration: 0.35 }}
        onClick={(e) => e.stopPropagation()}
        className="glass-strong rounded-2xl w-full max-w-md p-6 md:p-8"
        data-testid="service-editor"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="font-numbers text-[10px] tracking-[0.4em] uppercase text-gold">
              {isNew ? "New Service" : "Edit Service"}
            </div>
            <div className="font-heading text-2xl mt-1">
              {isNew ? "Add to catalog" : initial.name}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="close-editor"
            className="p-2 text-white/50 hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-6 space-y-5">
          <div>
            <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
              Name
            </label>
            <input
              className="input-minimal"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="editor-name"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
                Duration (min)
              </label>
              <input
                type="number"
                className="input-minimal font-numbers"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                data-testid="editor-duration"
              />
            </div>
            <div>
              <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
                Price (₹)
              </label>
              <input
                type="number"
                className="input-minimal font-numbers"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                data-testid="editor-price"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
                Sort Order
              </label>
              <input
                type="number"
                className="input-minimal font-numbers"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                data-testid="editor-sort"
              />
            </div>
            <div>
              <label className="font-numbers text-[10px] tracking-[0.3em] uppercase text-white/40">
                Status
              </label>
              <button
                onClick={() => setIsActive((v) => !v)}
                data-testid="editor-status"
                className={`mt-2 w-full rounded-full py-2 px-3 text-xs border font-numbers tracking-[0.3em] uppercase ${isActive ? "border-emerald-400/50 text-emerald-400" : "border-white/20 text-white/40"}`}
              >
                {isActive ? "Active" : "Hidden"}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-end gap-3">
          <button onClick={onClose} className="btn-ghost text-sm">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-service"
            className="btn-gold inline-flex items-center gap-2 text-sm py-2.5 px-5"
          >
            {saveLabel()}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
